import {
    ArrowLeft, ArrowLeftRight, Boxes, ClipboardCheck, ClipboardList, Contact, FileText, FileWarning, Package, PackageOpen,
    Inbox, PieChart, Scale, Settings, Tags, Users, Wrench,
} from "lucide-react";
import { etiquetaSeccion, puedeVerSeccion, SECCIONES_TECNICA } from "./tecnicaPermisos";
import { SOLAPAS_STOCK, TECNICA_BASE } from "./tecnicaRutas";

/**
 * Las secciones del menú de Técnica (TecnicaNavbar), con la forma de PortalAdminSidebar:
 * [{ key, title, items: [{ key, label, to, Icon }] }]. No hay buildRoute porque Técnica tiene un
 * solo prefijo: la ruta de cada ítem sale de SECCIONES_TECNICA. Es puro para poder probar qué ve
 * cada perfil sin montar nada.
 */

const ICONOS_STOCK = {
    saldos: Package,
    remitos: FileText,
    movimientos: ArrowLeftRight,
    catalogo: Tags,
    herramientas: Wrench,
    kits: Boxes,
    tecnicos: Users,
};

const item = (p, id, Icon) => ({ key: id, label: etiquetaSeccion(p, id), to: SECCIONES_TECNICA[id].ruta, Icon });

/** Deja sólo los ítems que la persona puede ver. */
const visibles = (p, ids, icono) => ids.filter((id) => puedeVerSeccion(p, id)).map((id) => item(p, id, icono(id)));

/**
 * @returns {Array<{ key: string, title: (string|null), items: Array<{ key: string, label: string, to: string, Icon: any }> }>}
 *   Sólo las secciones con algún ítem visible; «Menú principal» va siempre primero, bajo «General».
 */
export function seccionesMenuTecnica(p) {
    // Solicitudes va primero y «Órdenes» justo debajo: la solicitud es el paso previo a la orden (se carga, se
    // verifica y de ahí sale). «Nueva orden» no es un ítem para nadie, ni el admin: se llega desde la solicitud,
    // la ficha del cliente o el botón de la lista de órdenes. Al técnico se le distingue el ícono además del
    // label (son las suyas, no todas).
    const ordenes = visibles(p, ["solicitudes", "ordenes"], (id) => ({
        solicitudes: Inbox,
        ordenes: p.esTecnico ? ClipboardCheck : ClipboardList,
    })[id]);

    // Mi stock sólo para el técnico (miStockEnMenu): a los demás el backend les diría que no tienen legajo con stock.
    const stock = [
        ...(p.miStockEnMenu ? [item(p, "miStock", PackageOpen)] : []),
        ...visibles(p, SOLAPAS_STOCK, (id) => ICONOS_STOCK[id]),
        // Incidentes tiene ruta propia (no es una solapa): va después de las solapas del stock.
        ...visibles(p, ["incidentes"], () => FileWarning),
        // Diferencias (tanda 8), sólo Gestión: lo que se cargó en una OT sin saldo, para regularizar o anular.
        ...visibles(p, ["diferencias"], () => Scale),
    ];

    const clientes = visibles(p, ["clientes"], () => Contact);

    // Estadísticas va al final, arriba de Configuración: es global, no de una sección en particular.
    const administracion = visibles(p, ["estadisticas", "configuracion"], (id) => ({
        estadisticas: PieChart,
        configuracion: Settings,
    })[id]);

    return [
        // «General» no es decorativo: en escritorio el botón de colapsar está arriba a la derecha, y si
        // el primer hijo del menú era el link, el chevron le tapaba el borde (un clic ahí colapsaba en
        // vez de volver). Con un título primero pasa lo del Navbar general: el título queda debajo.
        { key: "volver", title: "General", items: [{ key: "volver", label: "Menú principal", to: "/home", Icon: ArrowLeft }] },
        // Clientes va arriba del todo: es la base sobre la que se cargan las órdenes.
        { key: "clientes", title: "Clientes", items: clientes },
        // El título no puede ser «Órdenes»: repetiría el nombre del ítem que tiene debajo. Quien sólo ve Solicitudes
        // (alcance «solo solicitudes») no tiene órdenes que nombrar.
        p.esTecnico
            ? { key: "ordenes", title: "Órdenes", items: ordenes }
            : { key: "solicitudes", title: p.soloSolicitudes ? "Solicitudes" : "Solicitudes y órdenes", items: ordenes },
        { key: "stock", title: "Stock", items: stock },
        { key: "administracion", title: "Administración", items: administracion },
    ].filter((s) => s.items.length > 0);
}

const bajo = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

/**
 * ¿El ítem corresponde a la pantalla actual? No es un prefijo simple (por eso el menú usa Link y
 * no NavLink): «Nueva orden» vive debajo de /ordenes, y la ficha de un artículo vuelve al Catálogo.
 */
export function itemActivo(item, pathname = "") {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const ordenes = `${TECNICA_BASE}/ordenes`;
    switch (item.key) {
        case "volver":
            return false;
        case "ordenes":
            // También «Nueva orden», que no tiene ítem propio en el menú.
            return bajo(path, ordenes);
        case "configuracion":
            return bajo(path, `${TECNICA_BASE}/configuracion`);
        case "catalogo":
            return bajo(path, `${TECNICA_BASE}/stock/catalogo`) || bajo(path, `${TECNICA_BASE}/stock/articulo`);
        default:
            return bajo(path, item.to);
    }
}
